import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AdminHeader } from '../AdminLayout'
import { TeamEditor } from '../components/TeamEditor'
import { CollectionEditor, FormActions } from '../components/CollectionEditor'
import { FaqEditor } from '../components/FaqEditor'
import { I18nField, SwitchField, TextField, type I18nValue } from '../components/fields'
import { MediaField } from '../components/MediaField'
import { useAdminContext } from '../session'
import { useCollection } from '../useCollection'
import type { ContentBlock, I18n, Sponsor } from '../types'

/**
 * Les textes du site et les partenaires.
 *
 * Tout ce que le visiteur lit d'éditorial vient d'ici : le concept, le code
 * vestimentaire, la politique de masque. Rien n'est écrit dans le code — c'est
 * la règle qui a présidé à tout le projet, et cet écran est l'endroit où elle
 * se vérifie.
 *
 * L'**ordre compte** : l'accueil ne montre que les deux premiers blocs, le
 * reste part dans « Infos ». Un bloc déplacé change donc ce que voit un
 * visiteur qui n'ouvre que la page d'accueil.
 */
export function AdminContentPage() {
  const { t, i18n } = useTranslation()
  const { eventId } = useAdminContext()

  useEffect(() => {
    document.title = t('admin.section.content')
  }, [t])

  const blocks = useCollection<ContentBlock>({
    key: 'admin-content-blocks',
    path: `/admin/events/${eventId}/content-blocks`,
    eventId,
    related: [['public-content']],
  })
  const sponsors = useCollection<Sponsor>({
    key: 'admin-sponsors',
    path: `/admin/events/${eventId}/sponsors`,
    eventId,
    related: [['public-sponsors']],
  })

  function label(value: I18n): string {
    return value[i18n.language] || value[Object.keys(value)[0] ?? ''] || '—'
  }

  return (
    <>
      <AdminHeader
        title={t('admin.section.content')}
        description={t('admin.content.description')}
      />

      <div className="grid gap-6">
        <CollectionEditor<ContentBlock>
          title={t('admin.content.blocks')}
          description={t('admin.content.blocksHint')}
          items={blocks.items}
          busy={blocks.busy}
          error={blocks.error}
          emptyLabel={t('admin.content.noBlocks')}
          createLabel={t('admin.content.addBlock')}
          onDelete={(block) => blocks.remove.mutate({ id: block.id })}
          onReorder={(ids) => blocks.reorder.mutate({ ids })}
          summarize={(block, index = 0) => {
            // Un document légal encore truffé de crochets est en ligne et
            // illisible : le dire dans la liste vaut mieux que de compter sur
            // quelqu'un pour ouvrir chaque texte avant l'ouverture des ventes.
            const gaps = countPlaceholders(block)
            return {
              title: label(block.title_i18n) || block.key,
              subtitle: block.key,
              value: index < 2 ? t('admin.content.onHome') : '',
              dimmed: !block.is_visible,
              badges: [
                ...(block.is_visible ? [] : [{ label: t('admin.hidden') }]),
                ...(gaps > 0
                  ? [{ label: t('admin.content.toFill', { count: gaps }), tone: 'warning' as const }]
                  : []),
              ],
            }
          }}
          renderForm={(block, close) => (
            <BlockForm
              block={block}
              busy={blocks.busy}
              onCancel={close}
              onSubmit={(payload) => {
                const done = { onSuccess: () => close() }
                if (block) blocks.update.mutate({ id: block.id, ...payload }, done)
                else blocks.create.mutate(payload, done)
              }}
            />
          )}
        />

        {/* Les questions fréquentes, juste après les blocs : elles finissent
            sur la même page « Infos » du site, et c'est en relisant l'une
            qu'on s'aperçoit qu'il manque l'autre. */}
        <FaqEditor eventId={eventId} />

        <CollectionEditor<Sponsor>
          title={t('admin.content.sponsors')}
          items={sponsors.items}
          busy={sponsors.busy}
          error={sponsors.error}
          emptyLabel={t('admin.content.noSponsors')}
          createLabel={t('admin.content.addSponsor')}
          onDelete={(sponsor) => sponsors.remove.mutate({ id: sponsor.id })}
          onReorder={(ids) => sponsors.reorder.mutate({ ids })}
          summarize={(sponsor) => ({
            title: sponsor.name,
            subtitle: sponsor.tier ?? '',
            dimmed: !sponsor.is_visible,
          })}
          renderForm={(sponsor, close) => (
            <SponsorForm
              sponsor={sponsor}
              busy={sponsors.busy}
              onCancel={close}
              onSubmit={(payload) => {
                const done = { onSuccess: () => close() }
                if (sponsor) sponsors.update.mutate({ id: sponsor.id, ...payload }, done)
                else sponsors.create.mutate(payload, done)
              }}
            />
          )}
        />

        {/* Les visages de la soirée. Rangés avec les contenus éditoriaux et
            non avec les comptes : c'est une page du site qu'on compose, pas
            une liste de droits qu'on attribue. */}
        <TeamEditor eventId={eventId} />
      </div>
    </>
  )
}

/**
 * Les marqueurs restés à compléter dans un texte.
 *
 * Les documents légaux sont semés avec des crochets aux endroits que personne
 * d'autre que l'organisateur ne peut remplir : raison sociale, hébergeur,
 * délai de suppression. Les inventer l'exposerait ; les laisser passer
 * silencieusement aussi. On les compte, et la liste le dit.
 */
function countPlaceholders(block: ContentBlock): number {
  const joined = Object.values(block.body_i18n ?? {}).join(' ')
  return new Set(joined.match(/\[[A-ZÀ-Ÿ0-9 ,'’/°:.-]{4,}\]/g) ?? []).size
}

function BlockForm({
  block,
  busy,
  onSubmit,
  onCancel,
}: {
  block: ContentBlock | null
  busy: boolean
  onSubmit: (payload: Record<string, unknown>) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [key, setKey] = useState(block?.key ?? '')
  const [title, setTitle] = useState<I18nValue>(block?.title_i18n ?? {})
  const [subtitle, setSubtitle] = useState<I18nValue>(block?.subtitle_i18n ?? {})
  const [body, setBody] = useState<I18nValue>(block?.body_i18n ?? {})
  const [ctaLabel, setCtaLabel] = useState<I18nValue>(block?.cta_label_i18n ?? {})
  const [ctaUrl, setCtaUrl] = useState(block?.cta_url ?? '')
  const [visible, setVisible] = useState(block?.is_visible ?? true)

  // La clé identifie le bloc dans le code du site (`mask_policy` habille le
  // hero, par exemple) : elle se fige à la création.
  const keyIsFixed = block !== null

  return (
    <div>
      <div className="grid gap-5 sm:grid-cols-2">
        {keyIsFixed ? null : (
          <TextField
            label={t('admin.content.key')}
            hint={t('admin.content.keyHint')}
            value={key}
            onChange={(value) => setKey(value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
          />
        )}
        <I18nField label={t('admin.content.blockTitle')} value={title} onChange={setTitle} />
        <I18nField label={t('admin.content.subtitle')} value={subtitle} onChange={setSubtitle} />
        <div className="sm:col-span-2">
          <I18nField label={t('admin.content.body')} multiline value={body} onChange={setBody} />
        </div>
        <I18nField label={t('admin.content.ctaLabel')} value={ctaLabel} onChange={setCtaLabel} />
        <TextField
          label={t('admin.content.ctaUrl')}
          type="url"
          value={ctaUrl}
          onChange={setCtaUrl}
        />
        <SwitchField label={t('admin.food.visible')} value={visible} onChange={setVisible} />
      </div>
      <FormActions
        busy={busy || (!keyIsFixed && key.length < 2)}
        onCancel={onCancel}
        onSubmit={() =>
          onSubmit({
            ...(keyIsFixed ? {} : { key }),
            title_i18n: title,
            subtitle_i18n: subtitle,
            body_i18n: body,
            cta_label_i18n: ctaLabel,
            cta_url: ctaUrl || null,
            is_visible: visible,
          })
        }
      />
    </div>
  )
}

function SponsorForm({
  sponsor,
  busy,
  onSubmit,
  onCancel,
}: {
  sponsor: Sponsor | null
  busy: boolean
  onSubmit: (payload: Record<string, unknown>) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const { eventId } = useAdminContext()
  const [name, setName] = useState(sponsor?.name ?? '')
  const [description, setDescription] = useState<I18nValue>(sponsor?.description_i18n ?? {})
  const [url, setUrl] = useState(sponsor?.url ?? '')
  const [tier, setTier] = useState(sponsor?.tier ?? '')
  const [visible, setVisible] = useState(sponsor?.is_visible ?? true)
  const [logoId, setLogoId] = useState<string | null>(sponsor?.logo_media_id ?? null)

  return (
    <div>
      <div className="grid gap-5 sm:grid-cols-2">
        <TextField label={t('admin.food.name')} value={name} onChange={setName} />
        <TextField label={t('admin.content.sponsorUrl')} type="url" value={url} onChange={setUrl} />
        <I18nField
          label={t('admin.food.descriptionField')}
          value={description}
          onChange={setDescription}
        />
        <TextField
          label={t('admin.content.tier')}
          hint={t('admin.content.tierHint')}
          value={tier}
          onChange={setTier}
        />
        <SwitchField label={t('admin.food.visible')} value={visible} onChange={setVisible} />
        <div className="sm:col-span-2">
          {/* Un partenaire se reconnaît à son logo avant son nom : c'est la
              première chose qu'il cherche sur la page, et souvent la seule
              contrepartie qu'on lui a promise. */}
          <MediaField
            label={t('admin.content.sponsorLogo')}
            hint={t('admin.content.sponsorLogoHint')}
            value={logoId}
            eventId={eventId}
            onChange={setLogoId}
          />
        </div>
      </div>
      <FormActions
        busy={busy || !name.trim()}
        onCancel={onCancel}
        onSubmit={() =>
          onSubmit({
            name: name.trim(),
            description_i18n: description,
            url: url || null,
            tier: tier || null,
            logo_media_id: logoId,
            is_visible: visible,
          })
        }
      />
    </div>
  )
}
